/**
 * PushDeliveryConfig - Shared delivery configuration for push notifications
 * Used by both PushQuickConfig and PushTemplateConfig
 */
import React from 'react';
import { useI18n } from '@/i18n';
import type { NodeData } from '@/types/imageWorkflow';

interface PushDeliveryConfigProps {
  nodeData: NodeData;
  isDisabled?: boolean;
  onUpdate: (field: string, value: unknown) => void;
}

export const PushDeliveryConfig: React.FC<PushDeliveryConfigProps> = ({
  nodeData,
  isDisabled = false,
  onUpdate,
}) => {
  const { t } = useI18n();
  const pushTargetType = (nodeData.push_target_type as string) || 'follow_start';

  return (
    <div className="space-y-4">
      {/* Target Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('imageWorkflows.pushTarget.pushTarget')}
        </label>
        <div className="space-y-2">
          <label className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-rose-50 border border-rose-200 bg-rose-50/30">
            <input
              type="radio"
              name="push_target"
              checked={pushTargetType === 'follow_start'}
              onChange={() => onUpdate('push_target_type', 'follow_start')}
              disabled={isDisabled}
              className="w-4 h-4 text-rose-600 border-gray-300 focus:ring-rose-500"
            />
            <div>
              <div className="text-sm font-medium text-rose-700">
                {t('imageWorkflows.pushTarget.followStartConfig')}
              </div>
              <p className="text-xs text-rose-500">
                {t('imageWorkflows.pushTarget.followStartConfigDesc')}
              </p>
            </div>
          </label>

          <label className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-gray-50 border border-gray-200">
            <input
              type="radio"
              name="push_target"
              checked={pushTargetType === 'current_user'}
              onChange={() => onUpdate('push_target_type', 'current_user')}
              disabled={isDisabled}
              className="w-4 h-4 text-rose-600 border-gray-300 focus:ring-rose-500"
            />
            <div>
              <div className="text-sm font-medium text-gray-700">
                {t('imageWorkflows.pushTarget.currentUser')}
              </div>
              <p className="text-xs text-gray-500">
                {t('imageWorkflows.pushTarget.currentUserDesc')}
              </p>
            </div>
          </label>

          <label className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-gray-50 border border-gray-200">
            <input
              type="radio"
              name="push_target"
              checked={pushTargetType === 'all_users'}
              onChange={() => onUpdate('push_target_type', 'all_users')}
              disabled={isDisabled}
              className="w-4 h-4 text-rose-600 border-gray-300 focus:ring-rose-500"
            />
            <div>
              <div className="text-sm font-medium text-gray-700">
                {t('imageWorkflows.pushTarget.allUsers')}
              </div>
              <p className="text-xs text-gray-500">
                {t('imageWorkflows.pushTarget.allUsersDesc')}
              </p>
            </div>
          </label>

          <label className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-gray-50 border border-gray-200">
            <input
              type="radio"
              name="push_target"
              checked={pushTargetType === 'specific_users'}
              onChange={() => onUpdate('push_target_type', 'specific_users')}
              disabled={isDisabled}
              className="w-4 h-4 text-rose-600 border-gray-300 focus:ring-rose-500"
            />
            <div>
              <div className="text-sm font-medium text-gray-700">
                {t('imageWorkflows.pushTarget.specificUsers')}
              </div>
              <p className="text-xs text-gray-500">
                {t('imageWorkflows.pushTarget.specificUsersDesc')}
              </p>
            </div>
          </label>
        </div>
      </div>

      {/* User IDs for specific_users mode */}
      {pushTargetType === 'specific_users' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('imageWorkflows.pushTarget.userIdList')}{' '}
            <span className="text-red-500">*</span>
          </label>
          <textarea
            value={((nodeData.push_target_user_ids as string[]) || []).join('\n')}
            onChange={(e) => {
              const ids = e.target.value
                .split('\n')
                .map((id) => id.trim())
                .filter(Boolean);
              onUpdate('push_target_user_ids', ids);
            }}
            rows={4}
            disabled={isDisabled}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-rose-500 focus:border-rose-500 disabled:bg-gray-100"
            placeholder={t('imageWorkflows.pushTarget.userIdListPlaceholder')}
          />
          <p className="text-xs text-gray-500 mt-1">
            {t('imageWorkflows.pushTarget.userIdListHint')}
          </p>
        </div>
      )}

      {/* Sound Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('imageWorkflows.pushTarget.notificationSound')}
        </label>
        <select
          value={(nodeData.push_sound as string) || 'default'}
          onChange={(e) => onUpdate('push_sound', e.target.value)}
          disabled={isDisabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-rose-500 focus:border-rose-500 disabled:bg-gray-100"
        >
          <option value="default">{t('imageWorkflows.pushTarget.default')}</option>
          <option value="none">{t('imageWorkflows.pushTarget.silent')}</option>
        </select>
      </div>

      {/* Badge Number */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('imageWorkflows.pushTarget.badgeNumber')}
        </label>
        <input
          type="number"
          min="0"
          value={(nodeData.push_badge as number) ?? ''}
          onChange={(e) =>
            onUpdate('push_badge', e.target.value ? parseInt(e.target.value) : undefined)
          }
          disabled={isDisabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-rose-500 focus:border-rose-500 disabled:bg-gray-100"
          placeholder={t('imageWorkflows.pushTarget.badgePlaceholder')}
        />
      </div>
    </div>
  );
};

export default PushDeliveryConfig;
